import * as flatbuffers from "flatbuffers";
import { tracePointEmit } from "../../integrationTrace";
import { TracePointId } from "../generated/integration-trace/trace-point-id";

import { UiCmdT } from "../generated/ui-cmd/ui-cmd";
import { ControlMessageT } from "../generated/control/control-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { ControlVariant } from "../generated/control/control-variant";
import { NormalControlT } from "../generated/control/normal-control";
import { Control } from "../generated/control/control";
import { UuidT } from "../generated/front-end/uuid";
import { U128T } from "../generated/u128/u128";

function uuidToBackendU128(uuid: string): { upper: bigint; lower: bigint } {
  const hex = uuid.replace(/-/g, "");
  if (hex.length !== 32) throw new Error(`Invalid UUID format: ${uuid}`);

  // The backend's U128 is the UUID's 16 bytes read big-endian: `upper` holds
  // time_low, time_mid and time_hi (the first 16 hex digits), `lower` holds
  // clock_seq and node (the last 16). Each field already sits at its bit
  // offset within its half, so parsing the half whole is the packed value.
  return {
    upper: BigInt(`0x${hex.slice(0, 16)}`),
    lower: BigInt(`0x${hex.slice(16, 32)}`),
  };
}

function toUuidT(uuid: string): UuidT {
  const { upper, lower } = uuidToBackendU128(uuid);
  return new UuidT(new U128T(upper, lower));
}

/**
 * Serialises one elevator control command as a finished `UiCmd` FlatBuffer.
 *
 * The control message, channel, bank and (optional) lift ids and a timestamp
 * go into a single root table; the returned bytes are what the monitoring
 * socket sends for a command.
 *
 * @throws Error "Invalid UUID format" when channelId, bankId or a given liftId is not
 *   32 characters once dashes are removed (an empty id included), and SyntaxError
 *   when those characters are not hex digits.
 */
export default function createUiCmdPayload(params: {
  message: Control;
  position: number;

  active: boolean;
  channelId: string;
  bankId: string;
  liftId?: string;
}) {
  // Validated in this order so a bad id is reported as before: channel, bank, lift.
  const channelId = toUuidT(params.channelId);
  const bankId = toUuidT(params.bankId);
  const liftId = params.liftId ? toUuidT(params.liftId) : null;

  const uiCmd = new UiCmdT(
    new ControlMessageT(
      DeviceControlMessage.ElevatorControlMessage,
      new ElevatorControlMessageT(
        ControlVariant.NormalControl,
        new NormalControlT(params.message),
        params.position,
      ),
    ),
    params.active,
    channelId,
    liftId,
    bankId,
    BigInt(Date.now()),
  );

  const builder = new flatbuffers.Builder(1024);
  builder.finish(uiCmd.pack(builder));

  // TP 414: FrontendUiCmdBuilt — UiCmd FlatBuffer payload successfully built
  tracePointEmit(TracePointId.FrontendUiCmdBuilt);

  return builder.asUint8Array();
}
